import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { CommonPagination } from "../../common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import {
  DataTableDateRangeFilterPopover,
  DataTableDropdownFilterPopover,
  DataTableStringFilterPopover,
  DataTableNumberRangeFilterPopover,
} from "./table-filters";
import type {
  DataTableCellConfig,
  DataTableDateRangeFilter,
  DataTableFilterConfig,
  DataTableDropdownFilter,
  DataTableNumberRangeFilter,
  DataTableSortConfig,
  DataTableStringFilter,
} from "./types";
import { TableErrorState } from "./TableErrorState";
import { renderDataTableCell } from "./TableCellRenderer";
import { TableLoadingState } from "./TableLoadingState";

export type DataTableColumn<T> = {
  key: string;
  title: React.ReactNode;
  filter?: DataTableFilterConfig | React.ReactElement<DataTableCustomFilterProps>;
  sort?: DataTableSortConfig;
  render?: (record: T, index: number) => React.ReactNode;
  cell?: DataTableCellConfig<T>;
  width?: string | number;
  minWidth?: string | number;
  headerClassName?: string;
  cellClassName?: string;
  align?: "left" | "center" | "right";
};

type DataTableCustomFilterProps = {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
};

export type DataTablePagination = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  dataSource: T[];
  rowKey: string | ((record: T, index: number) => string);
  loading?: boolean;
  loadingLabel?: React.ReactNode;
  error?: React.ReactNode;
  errorTitle?: React.ReactNode;
  errorDescription?: React.ReactNode;
  retryLabel?: React.ReactNode;
  onRetry?: () => void;
  showRetryButton?: boolean;
  emptyLabel?: React.ReactNode | null;
  containerClassName?: string;
  tableClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string | ((record: T, index: number) => string);
  onRowClick?: (record: T, index: number) => void;
  expandedRowKey?: string | null;
  renderExpandedRow?: (record: T, index: number) => React.ReactNode;
  expandedRowClassName?: string;
  pagination?: DataTablePagination;
  paginationClassName?: string;
}

function resolveRowKey<T>(rowKey: DataTableProps<T>["rowKey"], record: T, index: number) {
  return typeof rowKey === "function"
    ? rowKey(record, index)
    : (record as Record<string, unknown>)[rowKey]?.toString() || `${rowKey}-${index}`;
}

function isFilterActive(filter: DataTableFilterConfig) {
  switch (filter.type) {
    case "string":
      return filter.value.trim() !== "";
    case "range_number":
      return filter.value.min !== null || filter.value.max !== null;
    case "range_date":
      return filter.value.from.trim() !== "" || filter.value.to.trim() !== "";
    case "dropdown":
      return filter.value.trim() !== "";
    default:
      return false;
  }
}

function renderFilter(
  filter: DataTableColumn<unknown>["filter"],
  isOpen: boolean,
  onOpenChange: (open: boolean) => void,
) {
  if (!filter) {
    return null;
  }

  if (typeof filter === "object" && "type" in filter && "onFilter" in filter) {
    switch (filter.type) {
      case "string":
        return (
          <DataTableStringFilterPopover
            filter={filter as DataTableStringFilter}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            showTrigger={false}
          />
        );
      case "range_number":
        return (
          <DataTableNumberRangeFilterPopover
            filter={filter as DataTableNumberRangeFilter}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            showTrigger={false}
          />
        );
      case "range_date":
        return (
          <DataTableDateRangeFilterPopover
            filter={filter as DataTableDateRangeFilter}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            showTrigger={false}
          />
        );
      case "dropdown":
        return (
          <DataTableDropdownFilterPopover
            filter={filter as DataTableDropdownFilter}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            showTrigger={false}
          />
        );
      default:
        return null;
    }
  }

  if (React.isValidElement(filter)) {
    return React.cloneElement(filter, {
      isOpen,
      onOpenChange,
      showTrigger: false,
    });
  }

  return filter;
}

function renderSort(sort: DataTableSortConfig) {
  const currentDirection = sort.direction ?? null;
  const nextDirection =
    currentDirection === "asc" ? "desc" : currentDirection === "desc" ? null : "asc";
  const active = currentDirection !== null;
  const Icon =
    currentDirection === "asc" ? ArrowUp : currentDirection === "desc" ? ArrowDown : ArrowUpDown;

  return (
    <button
      type="button"
      className={cn(
        "ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors",
        active
          ? "bg-emerald-50 text-emerald-600"
          : "text-gray-400 hover:bg-gray-100 hover:text-gray-600",
      )}
      onClick={() => sort.onSort(nextDirection)}
      aria-label={typeof sort.title === "string" ? `Sort by ${sort.title}` : "Sort column"}
    >
      <Icon className={cn("h-3 w-3", active && "opacity-100")} />
    </button>
  );
}

export function DataTable<T>(props: DataTableProps<T>) {
  const { t } = useTranslation();
  const {
    columns,
    dataSource,
    rowKey,
    loading = false,
    loadingLabel = t("common.loading"),
    error,
    errorTitle,
    errorDescription,
    retryLabel,
    onRetry,
    showRetryButton = true,
    emptyLabel = t("ui.no_data_available"),
    containerClassName,
    tableClassName,
    headerClassName,
    bodyClassName,
    rowClassName,
    onRowClick,
    expandedRowKey,
    renderExpandedRow,
    expandedRowClassName,
    pagination,
    paginationClassName,
  } = props;

  const rowCount = Math.max(columns.length, 1);
  const tableColumns = useMemo(() => columns, [columns]);
  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);
  const resolvedBodyClassName = cn(
    "[&_tr:hover]:bg-gray-50 dark:[&_tr:hover]:bg-gray-800",
    bodyClassName,
  );
  const hasError =
    error !== null &&
    error !== undefined &&
    error !== false &&
    !(typeof error === "string" && error.trim() === "");

  return (
    <div className="relative">
      <Table
        containerClassName={cn("overflow-auto table-scrollbar", containerClassName)}
        className={tableClassName}
      >
        <TableHeader className={headerClassName}>
          <TableRow>
            {tableColumns.map((column) => {
              const filter = column.filter;
              const sort = column.sort;
              const hasFilter = filter != null;
              const hasSort = sort != null;
              const isOpen = hasFilter ? activeFilterKey === column.key : false;
              const usesCustomFilterTrigger = React.isValidElement(filter);
              const filterIsActive =
                filter && typeof filter === "object" && "type" in filter && "onFilter" in filter
                  ? isFilterActive(filter as DataTableFilterConfig)
                  : false;

              return (
                <TableHead
                  key={column.key}
                  className={cn(
                    "sticky top-0 z-20 bg-white p-3 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:bg-gray-900",
                    column.headerClassName,
                    column.align === "center" && "text-center",
                    column.align === "right" && "text-right",
                  )}
                  style={{ width: column.width, minWidth: column.minWidth }}
                >
                  <div
                    className={cn(
                      "flex items-center gap-2 font-semibold text-[#6A7282]",
                      column.align === "center" && "justify-center",
                      column.align === "right" && "justify-end",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{column.title}</span>
                    <div className="ml-auto flex items-center gap-1">
                      {hasFilter && (
                        <>
                          {!usesCustomFilterTrigger && (
                            <button
                              type="button"
                              className={cn(
                                "inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors",
                                filterIsActive
                                  ? "bg-emerald-50 text-emerald-600"
                                  : "text-gray-400 hover:bg-gray-100 hover:text-gray-600",
                              )}
                              onClick={() => {
                                setActiveFilterKey(isOpen ? null : column.key);
                              }}
                              aria-label={t("table.toggle_filter", { column: column.key })}
                            >
                              <img
                                className={cn("w-3", filterIsActive && "opacity-100")}
                                src={MagnifyingGlass}
                                alt={t("common.search")}
                              />
                            </button>
                          )}
                          {renderFilter(filter, isOpen, (open) =>
                            setActiveFilterKey(open ? column.key : null),
                          )}
                        </>
                      )}
                      {hasSort && renderSort(sort)}
                    </div>
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>

        <TableBody className={resolvedBodyClassName}>
          {loading ? (
            <TableLoadingState
              colSpan={rowCount}
              label={loadingLabel}
            />
          ) : hasError ? (
            <TableErrorState
              colSpan={rowCount}
              title={errorTitle}
              description={errorDescription}
              error={error}
              retryLabel={retryLabel}
              onRetry={onRetry}
              showRetryButton={showRetryButton}
            />
          ) : dataSource.length === 0 && emptyLabel !== null && emptyLabel !== undefined ? (
            <TableRow>
              <TableCell colSpan={rowCount}>
                <div className="flex w-full flex-1 items-center justify-center">
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">{emptyLabel}</p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : dataSource.length === 0 ? null : (
            dataSource.map((record, index) => {
              const resolvedRowKey = resolveRowKey(rowKey, record, index);
              const isExpanded =
                expandedRowKey === resolvedRowKey && renderExpandedRow !== undefined;

              return (
                <React.Fragment key={resolvedRowKey}>
                  <TableRow
                    className={cn(
                      "transition-colors hover:bg-gray-50 dark:hover:bg-gray-800",
                      onRowClick && "cursor-pointer",
                      typeof rowClassName === "function"
                        ? rowClassName(record, index)
                        : rowClassName,
                    )}
                    style={{ height: "75px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRowClick?.(record, index);
                    }}
                  >
                    {tableColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn(
                          "py-0",
                          column.cellClassName,
                          column.align === "center" && "text-center",
                          column.align === "right" && "text-right",
                        )}
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.render
                          ? column.render(record, index)
                          : column.cell
                            ? renderDataTableCell(column.cell, record)
                            : null}
                      </TableCell>
                    ))}
                  </TableRow>
                  {isExpanded ? (
                    <TableRow className={expandedRowClassName}>
                      <TableCell
                        colSpan={rowCount}
                        className="p-0"
                      >
                        {renderExpandedRow?.(record, index)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>

      {pagination && pagination.totalPages > 1 && (
        <div className={cn("mt-4", paginationClassName)}>
          <CommonPagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
          />
        </div>
      )}
    </div>
  );
}
